import { useRef, useState } from "react";
import { Building2, ChevronLeft, ChevronRight, HeartHandshake, Pause, Pill, Play } from "lucide-react";
import { useSamples } from "./SamplesContext";
import { Wave } from "./IncomingCallCard";

const Eyebrow = ({ children }) => <p className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--orange)]">{children}</p>;
const H2 = ({ children }) => <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[var(--ink)] sm:text-4xl">{children}</h2>;

const AUDIENCES = [
  { key: "hospitals", icon: Building2, title: "For Hospitals & Clinics", points: ["Patients stay with you — better retention", "More follow-up visits", "Your name heard daily — brand recall", "Zero extra counter work"] },
  { key: "pharmacies", icon: Pill, title: "For Pharmacies", points: ["Refill customers come back to you", "A weekly refill list, ready every Monday", "Your pharmacy named in refill reminders"] },
  { key: "families", icon: HeartHandshake, title: "For Families", points: ["A reminder at every medicine time — whether it's your parent, child or spouse", "Calls can also go to a caregiver for anyone who needs help", "For children, calls go to the parent or caregiver.", "Family alerts — coming soon."] },
];

export function Audiences() {
  const [tab, setTab] = useState("hospitals");
  return (
    <section id="audiences" className="bg-[var(--lav)] px-5 py-20 sm:px-8 lg:py-28" data-testid="section-audiences">
      <div className="mx-auto max-w-6xl">
        <Eyebrow>Who it's for</Eyebrow>
        <H2>Good for patients. Good for the people who care for them.</H2>
        <p className="mt-4 max-w-2xl text-[var(--ink)]/70" data-testid="audiences-intro">Built for every clinic, hospital and pharmacy — and for patients and families of every age.</p>
        <div className="mt-10 hidden gap-2 md:flex" role="tablist">
          {AUDIENCES.map((a) => (
            <button key={a.key} role="tab" aria-selected={tab === a.key} onClick={() => setTab(a.key)} data-testid={`audience-tab-${a.key}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === a.key ? "bg-[var(--ink)] text-white" : "bg-white text-[var(--ink)] hover:bg-[var(--lav-2)]"}`}>
              {a.title}
            </button>
          ))}
        </div>
        <div className="mt-6 grid gap-4 md:mt-6">
          {AUDIENCES.map((a) => (
            <article key={a.key} className={`${tab === a.key ? "md:grid" : "md:hidden"} grid gap-6 rounded-3xl bg-white p-7 md:grid-cols-[0.7fr_1.3fr] md:p-10`} data-testid={`audience-panel-${a.key}`}>
              <div>
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[var(--orange)] text-white"><a.icon size={24} /></div>
                <h3 className="mt-4 text-2xl font-bold text-[var(--ink)]">{a.title}</h3>
              </div>
              <ul className="grid gap-3">
                {a.points.map((p) => (
                  <li key={p} className="flex gap-3 rounded-2xl bg-[var(--lav)] px-4 py-3.5 text-[var(--ink)]/85">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[var(--orange)]" />{p}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SamplePlayer() {
  const { samples, current, playing, play } = useSamples();
  const row = useRef(null);
  const scroll = (d) => row.current?.scrollBy({ left: d * 380, behavior: "smooth" });
  return (
    <section id="samples" className="bg-[var(--ink)] px-5 py-20 text-[var(--cream)] sm:px-8 lg:py-28" data-testid="section-samples">
      <div className="mx-auto max-w-6xl">
        <Eyebrow>Listen</Eyebrow>
        <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">Hear the calls your patients get.</h2>
        <div className="flex items-end justify-between gap-4">
          <p className="mt-3 max-w-xl text-[var(--cream)]/70">All are samples with made-up patients and made-up hospitals, marked <strong>(Sample)</strong>. Swipe to hear more.</p>
          <div className="hidden shrink-0 gap-2 md:flex">
            {[[-1, ChevronLeft, "prev"], [1, ChevronRight, "next"]].map(([d, Icon, t]) => (
              <button key={t} onClick={() => scroll(d)} aria-label={t === "prev" ? "Previous samples" : "More samples"} data-testid={`samples-${t}`}
                className="grid h-11 w-11 place-items-center rounded-full border border-white/20 transition-colors hover:bg-white/10"><Icon size={18} /></button>
            ))}
          </div>
        </div>
        <div ref={row} className="no-scrollbar -mx-5 mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-4 sm:-mx-8 sm:px-8" data-testid="samples-row">
          {samples.map((s, i) => {
            const on = playing && current === i;
            return (
              <article key={s.key} className="flex w-[85%] shrink-0 snap-start flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:w-[360px]" data-testid={`sample-card-${s.key}`}>
                <h3 className="text-xl font-bold">{s.label}</h3>
                {s.note && <p className="mt-2 border-l-2 border-[var(--orange)] pl-3 text-sm text-[var(--cream)]/75" data-testid={`sample-note-${s.key}`}>{s.note}</p>}
                <p className="mt-2 text-sm text-[var(--cream)]/60" data-testid={`sample-meta-${s.key}`}>{s.meta}</p>
                <p className="font-te mt-4 flex-1 text-[14px] text-[var(--cream)]/85" data-testid={`sample-text-${s.key}`}>{s.text}</p>
                <div className="mt-5 flex items-center gap-3">
                  <button onClick={() => play(i)} data-testid={`sample-play-${s.key}`} className="btn-orange grid h-12 w-12 place-items-center rounded-full" aria-label={on ? "Pause" : "Play"}>
                    {on ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                  </button>
                  <Wave on={on} n={14} />
                  {!s.audio_url && <span className="ml-auto text-xs text-[var(--cream)]/50">Voice coming soon</span>}
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  {s.voice_name && <p className="text-xs text-[var(--cream)]/50" data-testid={`sample-voice-${s.key}`}>Voice: {s.voice_name}</p>}
                  <span className="ml-auto rounded-full bg-[var(--orange)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid={`sample-badge-${s.key}`}>Sample</span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
